import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { IoApps } from "react-icons/io5";
import { Loading } from "..";
import useRouting, { Paths, useParams } from "../../contexts/Routing";
import { ErrorRemote } from "../../pages/error/Error";
import {
    GetUserDocument,
    GetUsersDocument,
    useDeleteUserMutation,
    useGetUserQuery, useSaveUserMutation,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import OrganizationPicker from "../pickers/organizations/Organizations";
import TagList from "../tag-list/TagList";
import TimedMessage from "../timed-message/TimedMessage";

export default function UserDetail(): ReactElement {
    const { id } = useParams(Paths.USERS_ID);

    const isNew = id === "new";
    const { historyPush, getSearchParams } = useRouting();
    const { companyId } = getSearchParams(Paths.USERS_NEW);

    const [deleteUser] = useDeleteUserMutation();
    const [isShowDelete, setShowDelete] = useState<boolean>(false);

    const [saveUser] = useSaveUserMutation();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [emails, setEmails] = useState<string[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [organizationId, setOrganizationId] = useState<string | undefined>(companyId);

    const { data, error, loading: isLoading } = useGetUserQuery({
        variables: {
            id,
        },
        skip: isNew,
    });

    useEffect(() => {
        if (data?.user) {
            setFirstName(data.user.firstName ?? "");
            setLastName(data.user.lastName ?? "");
            setOrganizationId(data.user.organizationId);
            setEmails(data?.user?.handles?.map(handle => handle.email?.email).filterNull() ?? []);
        }
    }, [data?.user]);

    useEffect(() => {
        setOrganizationId(companyId);
    }, [companyId]);

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <ErrorRemote error={error} />;
    }

    const handleOrganizationSelected = async (organization: string): Promise<void> => {
        setOrganizationId(organization);
    };

    const handleDeleteUser = async (userId: string) => {
        await deleteUser({
            refetchQueries: [
                { query: GetUsersDocument, variables: { companyId: organizationId } },
                { query: GetUserDocument, variables: { id: userId } },

            ],
            variables: {
                id: userId,
            },
        });

        const orgId = companyId ?? data?.user?.organizationId;
        if (orgId) {
            historyPush(Paths.ORGANIZATIONS_ID, { id: orgId });
        } else {
            historyPush(Paths.CONTACTS_ENTITY, { entity: "users" });
        }
    };

    const handleChangeUser = async (userId?: string): Promise<void> => {
        try {
            await saveUser({
                refetchQueries: [
                    { query: GetUsersDocument },
                    { query: GetUsersDocument, variables: { companyId: organizationId } },
                    { query: GetUserDocument, variables: { id: userId } },
                ],
                variables: {
                    id: userId,
                    user: {
                        firstName: firstName !== "" ? firstName : undefined,
                        lastName: lastName !== "" ? lastName : undefined,
                        organizationId,
                        emailAddresses: emails,
                    },
                },
            });

            historyPush(Paths.CONTACTS_ENTITY, { entity: "users" });
        } catch (err) {
            if (err instanceof ApolloError) {
                setErrorMessage(err.message);
                setIsError(true);
            }
        }
    };

    const applicationHandles = Object.entries(
        data?.user?.handles?.filter(handle => handle.application)
            .groupBy(app => app.application?.applicationName ?? "")
        ?? {},
    );

    return (
        <>

            <Confirm
                confirmText="Delete"
                onConfirm={() => {
                    handleDeleteUser(data?.user?.id);
                    setShowDelete(false);
                }}
                shouldShow={isShowDelete}
                closeText="Close"
                onHide={() => setShowDelete(false)}
            >
                <div>
                    Are you sure you want to remove this user?
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {data?.user?.name}
                    </div>
                </div>
            </Confirm>
            <div className="border p-3 rounded bg-white">
                <h5 className="text-light mb-3">
                    {isNew ? "Add a user" : "Edit user"}
                </h5>

                <div className="d-flex">

                    <form
                        className="flex-grow-1"
                        onSubmit={event => {
                            event.preventDefault();
                            handleChangeUser(isNew ? undefined : id);
                        }}
                    >
                        <TimedMessage
                            shouldShow={isError}
                            variant="danger"
                            onClose={() => {
                                setIsError(false);
                                setErrorMessage("");
                            }}
                        >
                            {errorMessage}
                        </TimedMessage>
                        <Form.Group className="mb-3">
                            <Form.Label className="my-1 fw-bolder" htmlFor="addFirstName">First Name</Form.Label>
                            <Form.Control
                                id="addFirstName"
                                className="form-control w-100 col"
                                type="text"
                                onChange={element => setFirstName(element.target.value)}
                                value={firstName}
                                required
                                autoFocus
                            />
                            <Form.Text>
                                The first name of the user
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder" htmlFor="addLastName">Last Name</Form.Label>
                            <Form.Control
                                id="addLastName"
                                className="form-control w-100 col"
                                type="text"
                                onChange={element => setLastName(element.target.value)}
                                value={lastName}
                                required
                            />
                            <Form.Text>
                                The last name of the user
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder" htmlFor="addLastName">Organization</Form.Label>
                            <OrganizationPicker
                                onSelected={handleOrganizationSelected}
                                minRequired={0}
                                className="w-100 col p-0"
                                value={organizationId}
                            />
                            <Form.Text>
                                The organization the user belongs to
                            </Form.Text>
                        </Form.Group>
                        <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                            {!isNew && (
                                <button
                                    type="button"
                                    className="btn btn-none text-danger me-3"
                                    onClick={() => setShowDelete(true)}
                                >
                                    Delete
                                </button>
                            )}

                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {isNew ? "Add" : "Save"}
                            </button>
                        </div>
                    </form>

                    <div className="text-light col-6 col-lg-4 col-xl-3 border-start px-4 ms-4 pt-4">
                        {isNew ? (
                            <p>
                                Create a new user by entering the first name, last name, email and which company this user
                                is associated with.
                            </p>
                        ) : (
                            <p>
                                You can modify the name and email here. The email address has to be unique for this user.
                                When you click save, Foqal will validate that this email is not associated with another
                                account.
                            </p>
                        )}

                    </div>
                </div>
            </div>

            <div className="mb-4">
                {emails && (
                    <div className="border p-3 rounded bg-white mt-4">
                        <div className="mb-3">
                            <h5 className="text-light mb-1">
                                Emails
                            </h5>
                            <small className="text-light">Email addresses associated with this user.</small>
                        </div>

                        <div className="list-group">

                            <TagList
                                canNotHaveDuplicates
                                isTag={false}
                                type="email"
                                placeholder="email@example..."
                                value={emails}
                                onChange={value => setEmails(value)}
                            />

                        </div>
                    </div>
                )}

                {applicationHandles.isNotEmpty && (
                    <div className="border p-3 rounded bg-white mt-4">
                        <div className="mb-3">
                            <h5 className="text-light mb-1">
                                Application Connections
                            </h5>
                            <small className="text-light">
                                This user is associated with other 3rd party applications. They are listed below.
                            </small>
                        </div>

                        {applicationHandles.map(([appName, handles]) => (
                            <div className="mb-3">
                                <h6>{appName}</h6>

                                <div className="list-group ">
                                    {handles.map(handle => (

                                        <div
                                            className="d-flex w-100 justify-content-between list-group-item align-items-center"
                                            key={handle.id}
                                        >
                                            <div className="d-flex flex-grow-1 align-items-center">
                                                {handle.application && (
                                                    <div className="d-flex align-items-center">
                                                        <IoApps className="text-light me-2" />
                                                        {handle.application.displayText}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                </div>
                            </div>
                        ))}

                    </div>
                )}
            </div>

        </>
    );
}
